/**
 * useFormGenerationStream: SSE form-generation orchestration hook.
 *
 * Manages the `/forms/generate` SSE stream lifecycle for the form-builder
 * wizard. Encapsulates:
 *   - `runGeneration`: starts an SSE stream, appends fields with a staggered
 *     reveal cadence, calls onTitle/onGoal callbacks, and surfaces errors.
 *   - `handleRetryGeneration`: re-runs the most recent generation attempt.
 *   - `abortGeneration`: cancels any in-flight stream and clears pending timers.
 *
 * The "fill only when empty" guard for title and goal lives in the component's
 * `onTitle` / `onGoal` callbacks, not in this hook, so the hook stays generic.
 *
 * @module components/hooks/useFormGenerationStream
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import type React from 'react';
import { streamFormGeneration } from '../../lib/sse-consumer';
import { resolveGeneratedGif } from '../../lib/generated-gif';
import type { SseProductResearch } from '../../lib/sse-consumer';
import type { FormImportReport } from '../../lib/form-import-report';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import type { FormField } from '../../types';
import type { FormTemplateKey } from '../../lib/form-templates';
import type { InlineAttachment } from '../../lib/file-attachment';
import { validateConversationSections, type ConversationSection } from '../../lib/conversation-sections';
import {
  GenerationRequestError,
  isFormGenerationErrorCode,
  isRetryableGenerationError,
  type FormGenerationErrorCode,
} from '../../lib/generation-errors';

/**
 * Keep the first few reveals staggered without delaying a completed draft
 * by one full interval per question.
 */
const FIELD_REVEAL_CADENCE_MS = 220;
/** Maximum queued reveal delay after a field has already arrived. */
const MAX_FIELD_REVEAL_BACKLOG_MS = 600;

/**
 * Input configuration for the useFormGenerationStream hook.
 */
export interface UseFormGenerationStreamInput {
  /** Definition version, read again on retry; new conversations use version 1. */
  multimodalVersion?: 1;
  /**
   * Brand-research payload derived from the active workspace. Threaded into
   * the worker's `/forms/generate` body.
   */
  productResearch: SseProductResearch | undefined;
  /** Supabase access token forwarded to the worker as a Bearer JWT. */
  accessToken: string;
  /** Active workspace id forwarded for PostHog error properties. */
  workspaceId: string | null;
  /** Active organization id forwarded for PostHog error properties. */
  organizationId: string | null;
  /**
   * Called when a `title` SSE event arrives with a non-empty trimmed value.
   * The "fill only when empty" guard should live in the caller's callback.
   */
  onTitle: (title: string) => void;
  /**
   * Called when a `goal` SSE event arrives with a non-empty trimmed value.
   * The "fill only when empty" guard should live in the caller's callback.
   */
  onGoal: (goal: string) => void;
  /** Safe authoring notes; null starts a new generation or retry without stale notes. */
  onImportReport?: (report: FormImportReport | null) => void;
  /** Publish visible group membership in the same render as each revealed field. */
  onSections?: (sections: ConversationSection[]) => void;
  /**
   * Fields present on the canvas at FIRST render (edit mode's saved schema,
   * a clone's copied fields, or a restored draft). Seeded via the state
   * initializer, so the canvas never paints an empty frame before an effect
   * tick fills it. Read once at mount; later changes are ignored.
   */
  initialFields?: FormField[];
}

/**
 * A generation failure as app state: a public code and whether retry is
 * truthful. Copy is resolved from the code at render time, never stored.
 */
export interface GenerationErrorState {
  /** Public code from `lib/generation-errors`. */
  code: FormGenerationErrorCode;
  /** Whether another attempt with the same input can change the outcome. */
  retryable: boolean;
}

/** Snapshot of arguments used for the last generation run. Used by handleRetryGeneration. */
export interface LastGenAttempt {
  objective: string;
  template: FormTemplateKey;
  attachment: InlineAttachment | null;
  objectiveHint?: string;
  /** Selected card identity, replayed only with this generation attempt. */
  templateId?: string;
}

/**
 * Return value from useFormGenerationStream.
 */
export interface UseFormGenerationStreamResult {
  /** Current list of revealed form fields. */
  fields: FormField[];
  /** Setter for fields (allows the component to clear on back navigation). */
  setFields: React.Dispatch<React.SetStateAction<FormField[]>>;
  /** True while the SSE stream is running or stagger timers are still firing. */
  isGenerating: boolean;
  /** Setter for isGenerating. */
  setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>;
  /** True from stream start until the title SSE event lands or stream ends. */
  awaitingTitle: boolean;
  /** Setter for awaitingTitle. */
  setAwaitingTitle: React.Dispatch<React.SetStateAction<boolean>>;
  /** Current generation failure, or null when no error. */
  generationError: GenerationErrorState | null;
  /** Setter for generationError. */
  setGenerationError: React.Dispatch<React.SetStateAction<GenerationErrorState | null>>;
  /**
   * Run SSE form generation for the given objective and template. Manages
   * stagger timers and surfaces errors visibly.
   */
  runGeneration: (
    objective: string,
    template: FormTemplateKey,
    attachment?: InlineAttachment | null,
    objectiveHint?: string,
    templateId?: string,
  ) => Promise<void>;
  /** Retry the most recent generation attempt with the same arguments. */
  handleRetryGeneration: () => void;
  /** Cancel any in-flight stream and clear all pending stagger timers. */
  abortGeneration: () => void;
  /**
   * Ref holding the most recent generation args for the retry handler.
   * Exposed so the component can clear it (e.g., on handleStartFromScratch).
   */
  lastGenAttemptRef: React.MutableRefObject<LastGenAttempt | null>;
}

/**
 * Manages the SSE /forms/generate stream lifecycle and staggered field reveal
 * for the CreateInterviewForm wizard.
 *
 * @param input - Configuration including brand context, auth token, and callbacks.
 * @returns Stream state, setters, and control callbacks.
 */
export function useFormGenerationStream(
  input: UseFormGenerationStreamInput,
): UseFormGenerationStreamResult {
  const { productResearch, accessToken, workspaceId, organizationId, onTitle, onGoal } = input;

  const [fields, setFields] = useState<FormField[]>(input.initialFields ?? []);
  const [isGenerating, setIsGenerating] = useState(false);
  const [awaitingTitle, setAwaitingTitle] = useState(false);
  const [generationError, setGenerationError] = useState<GenerationErrorState | null>(null);

  /** Args from the most recent generation call, used by handleRetryGeneration. */
  const lastGenAttemptRef = useRef<LastGenAttempt | null>(null);

  /** Cancellation for in-flight SSE streams. */
  const generateAbortRef = useRef<AbortController | null>(null);

  /**
   * Preferred next reveal time, capped when a burst would build a long queue.
   */
  const nextRevealSlotRef = useRef<number>(0);

  /** Pending stagger timers tracked so they can be cancelled on abort or unmount. */
  const revealTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  /** Refs captured for callbacks to avoid stale closures in useCallback. */
  const onTitleRef = useRef(onTitle);
  onTitleRef.current = onTitle;
  const onGoalRef = useRef(onGoal);
  onGoalRef.current = onGoal;
  const onImportReportRef = useRef(input.onImportReport);
  onImportReportRef.current = input.onImportReport;
  const onSectionsRef = useRef(input.onSections);
  onSectionsRef.current = input.onSections;
  const productResearchRef = useRef(productResearch);
  productResearchRef.current = productResearch;
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;
  const workspaceIdRef = useRef(workspaceId);
  workspaceIdRef.current = workspaceId;
  const organizationIdRef = useRef(organizationId);
  organizationIdRef.current = organizationId;
  const multimodalVersionRef = useRef(input.multimodalVersion);
  multimodalVersionRef.current = input.multimodalVersion;

  const abortGeneration = useCallback(() => {
    const controller = generateAbortRef.current;
    generateAbortRef.current = null;
    controller?.abort();
    for (const t of revealTimersRef.current) clearTimeout(t);
    revealTimersRef.current.clear();
    nextRevealSlotRef.current = 0;
  }, []);

  useEffect(() => abortGeneration, [abortGeneration]);

  const runGeneration = useCallback(
    async (
      selectedObjective: string,
      selectedTemplate: FormTemplateKey,
      attachment: InlineAttachment | null = null,
      objectiveHint: string | undefined = undefined,
      templateId: string | undefined = undefined,
    ) => {
      lastGenAttemptRef.current = {
        objective: selectedObjective,
        template: selectedTemplate,
        attachment,
        objectiveHint,
        templateId,
      };

      generateAbortRef.current?.abort();
      const controller = new AbortController();
      generateAbortRef.current = controller;
      for (const t of revealTimersRef.current) clearTimeout(t);
      revealTimersRef.current.clear();
      nextRevealSlotRef.current = 0;

      setFields([]);
      onSectionsRef.current?.([]);
      onImportReportRef.current?.(null);
      setGenerationError(null);
      setIsGenerating(true);
      const generationWorkspaceId = workspaceIdRef.current;
      const revealedFieldIds = new Set<string>();
      let receivedSections: ConversationSection[] | undefined;
      let sectionsValidated = false;

      /** Keep future headings hidden so already revealed questions never move into new wrappers. */
      const publishRevealedSections = (includeTrailingSections = false) => {
        if (!receivedSections || workspaceIdRef.current !== generationWorkspaceId) return;
        const visibleSections = receivedSections.flatMap(section => {
          if (section.fieldIds.length) {
            const fieldIds = section.fieldIds.filter(id => revealedFieldIds.has(id));
            return fieldIds.length ? [{ ...section, fieldIds }] : [];
          }
          return (section.beforeFieldId ? revealedFieldIds.has(section.beforeFieldId) : includeTrailingSections) ? [section] : [];
        });
        onSectionsRef.current?.(visibleSections);
      };

      /**
       * Schedule a field append on the reveal cadence. The first field after a
       * fresh generation lands immediately. A burst retains the opening stagger
       * but caps the remaining queue so long drafts become usable promptly.
       */
      const scheduleStaggered = (field: FormField) => {
        const now = Date.now();
        const target = Math.min(Math.max(now, nextRevealSlotRef.current), now + MAX_FIELD_REVEAL_BACKLOG_MS);
        const delay = target - now;
        const timer = setTimeout(() => {
          revealTimersRef.current.delete(timer);
          if (controller.signal.aborted || generateAbortRef.current !== controller || workspaceIdRef.current !== generationWorkspaceId) return;
          revealedFieldIds.add(field.id);
          // React batches these writes, so a question's first frame already has its section.
          publishRevealedSections(sectionsValidated && revealedFieldIds.size === receivedFieldIds.size);
          setFields((prev) => prev.some((existing) => existing.id === field.id) ? prev : [...prev, field]);
        }, delay);
        revealTimersRef.current.add(timer);
        nextRevealSlotRef.current = target + FIELD_REVEAL_CADENCE_MS;
      };

      /**
       * Show a failure by code and retry decision. The worker captures what
       * it classifies, so only client-detected failures (`reason` given) are
       * captured here, with the stable code and no user input.
       */
      const surfaceError = (code: FormGenerationErrorCode, retryable: boolean, reason?: unknown) => {
        onImportReportRef.current?.(null);
        setGenerationError({ code, retryable });
        if (reason === undefined) return;
        captureException(reason instanceof Error ? reason : new Error(String(reason)), 'CreateInterviewForm.generateForm', {
          template: selectedTemplate,
          workspaceId: workspaceIdRef.current,
          organizationId: organizationIdRef.current,
          code,
        });
      };

      let receivedAny = false;
      const receivedFieldIds = new Set<string>();
      const receivedFields: FormField[] = [];
      let reportedImportCount: number | undefined;
      let completedCount: number | undefined;
      let errored = false;
      let aborted = false;

      try {
        for await (const event of streamFormGeneration(
          {
            ...(multimodalVersionRef.current ? { multimodalVersion: multimodalVersionRef.current, sectionsVersion: 1 as const } : {}),
            objective: selectedObjective,
            template: selectedTemplate,
            ...(templateId ? { templateId } : {}),
            objectiveHint,
            productResearch: productResearchRef.current,
            attachment: attachment
              ? {
                  mimeType: attachment.mimeType,
                  dataBase64: attachment.dataBase64,
                }
              : undefined,
          },
          controller.signal,
          { accessToken: accessTokenRef.current },
        )) {
          if (controller.signal.aborted || generateAbortRef.current !== controller) { aborted = true; break; }
          if (event.type === 'field') {
            receivedAny = true;
            const field = event.gifQuery ? await resolveGeneratedGif(event.field, event.gifQuery, controller.signal, {
              objective: selectedObjective, accessToken: accessTokenRef.current,
            }) : event.field;
            if (controller.signal.aborted || generateAbortRef.current !== controller) { aborted = true; break; }
            receivedFieldIds.add(field.id);
            receivedFields.push(field);
            scheduleStaggered(field);
          } else if (event.type === 'sections') {
            receivedSections = event.sections;
          } else if (event.type === 'import_report') {
            reportedImportCount = event.report.importedCount;
            if (workspaceIdRef.current === generationWorkspaceId) onImportReportRef.current?.(event.report);
          } else if (event.type === 'done') {
            completedCount = event.count;
          } else if (event.type === 'title') {
            const incoming = event.title.trim();
            if (incoming) {
              onTitleRef.current(incoming);
            }
            setAwaitingTitle(false);
          } else if (event.type === 'goal') {
            const incomingGoal = event.goal.trim();
            if (incomingGoal) {
              onGoalRef.current(incomingGoal);
            }
          } else if (event.type === 'error') {
            errored = true;
            if (event.serverReported) surfaceError(event.code, event.retryable);
            else surfaceError(event.code, event.retryable, new Error(`Invalid generation event (${event.code})`));
            break;
          }
        }

        if (!errored && !aborted) {
          if (receivedSections !== undefined && (completedCount !== receivedFieldIds.size || validateConversationSections(receivedFields, receivedSections).length)) {
            errored = true;
            surfaceError('unusable_output', true, new Error('Generated section membership was invalid or incomplete'));
          } else if (reportedImportCount !== undefined && (completedCount !== reportedImportCount || receivedFieldIds.size !== reportedImportCount)) {
            errored = true;
            surfaceError('source_incomplete', isRetryableGenerationError('source_incomplete'), new Error('Import stream ended with a count mismatch'));
          } else if (!receivedAny && reportedImportCount === undefined) {
            // Without a done event the stream was cut off: a transport
            // failure, not a verdict on the model's output.
            if (completedCount === undefined) surfaceError('failed', true, new Error('SSE stream ended before its done event'));
            else surfaceError('unusable_output', true, new Error('SSE stream produced no fields'));
          }
          sectionsValidated = !errored && receivedSections !== undefined;
        }
      } catch (err) {
        if (controller.signal.aborted || generateAbortRef.current !== controller || (err as { name?: string }).name === 'AbortError') {
          aborted = true;
        } else if (err instanceof GenerationRequestError) {
          errored = true;
          // A non-OK response: its public code and retry decision, captured
          // here only when the worker did not classify it.
          const code = isFormGenerationErrorCode(err.code) ? err.code : 'failed';
          surfaceError(code, err.retryable, err.serverReported ? undefined : err);
        } else {
          errored = true;
          // Transport or unexpected client failure: its text is never shown.
          surfaceError('failed', true, err);
        }
      } finally {
        if (generateAbortRef.current !== controller) return;
        setAwaitingTitle(false);

        if (aborted || errored || !receivedAny) {
          if (aborted || errored) {
            for (const timer of revealTimersRef.current) clearTimeout(timer);
            revealTimersRef.current.clear();
          } else if (sectionsValidated) publishRevealedSections(true);
          setIsGenerating(false);
        } else {
          const tailDelay = Math.max(0, nextRevealSlotRef.current - Date.now());
          const tailTimer = setTimeout(() => {
            revealTimersRef.current.delete(tailTimer);
            if (controller.signal.aborted || generateAbortRef.current !== controller) return;
            if (sectionsValidated) publishRevealedSections(true);
            setIsGenerating(false);
          }, tailDelay);
          revealTimersRef.current.add(tailTimer);
        }
      }
    },
    [],
  );

  const handleRetryGeneration = useCallback(() => {
    const last = lastGenAttemptRef.current;
    if (!last) return;
    void runGeneration(last.objective, last.template, last.attachment, last.objectiveHint, last.templateId);
  }, [runGeneration]);

  return {
    fields,
    setFields,
    isGenerating,
    setIsGenerating,
    awaitingTitle,
    setAwaitingTitle,
    generationError,
    setGenerationError,
    runGeneration,
    handleRetryGeneration,
    abortGeneration,
    lastGenAttemptRef,
  };
}
